/**
 * HOW MANY DECKS A PHONE PAGE CAN ACTUALLY HOLD.
 *
 * The deck stage paged three choices at a time, always. On a landscape phone
 * the list is handed between 66px and 91px of room — one 44px row, sometimes
 * two — and the list clips (`overflow: hidden` on the phone wrapper), so the
 * rows past the first were drawn outside their own box and simply disappeared.
 * The owner saw it as "does not fit all decks on screen, they lay outside".
 *
 * The two obvious repairs are both refused. A scrollbar is refused because the
 * owner refused it, twice and in those words. Shrinking the row is refused
 * because 44px IS the tap target, and this file's whole point is that vertical
 * room is the scarce thing — spending it on an unhittable control gains
 * nothing. What is left is to page what fits, which the pager beneath the list
 * already knows how to walk.
 *
 * COMPUTED BEFORE THE FIRST PAINT, never corrected after one. The deck stage
 * rewrites its own `innerHTML`, so a measure-then-re-render would be exactly
 * the load-time layout mutation the repo forbids. Everything below is derived
 * from boxes that already exist when the render begins.
 */

/** The designed page. Nothing here ever shows more than this. */
const PHONE_DECK_MAX_PER_PAGE = 3;

/* Measured on the real deck stage in WebKit at 932x250 and 667x275. Each is a
   published number from the landscape band in `deck-selector.css`; the
   real-backend page-box spec fails if the CSS and these drift apart. */
const DECK_ROW_PX = 44;          // the tap target — never negotiable
const DECK_ROW_GAP_PX = 4;       // .ds-deck-list gap at phone type scale
const HEADER_BLOCK_PX = 52;      // .ds-header 44 min-height + 8 margin-bottom
const PAGER_BLOCK_PX = 52;       // #ds-deck-pagination
const PREFERENCES_MARGIN_PX = 6; // .battle-preferences margin-bottom
/* The battle-switch row's published height in the landscape band. It is a
   44px touch row like the deck rows, and the deck stage always carries it. */
const PHONE_PREFERENCES_ROW_PX = 44;

function finite(value) {
  // `Number(null)` is 0, not NaN — so a null room would read as "no space at
  // all" and page one deck on a desktop that has plenty. Reject the empty
  // values before coercing, the same trap the Moonlight meter documents.
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {object} [input]
 * @param {number|null} [input.listRoomPx] the list's own room, when it can be
 *   measured directly. Preferred over the derivation below.
 * @param {number|null} [input.contentHeightPx] the deck stage's content area.
 * @param {number|null} [input.preferencesHeightPx] the battle-switch row, 0
 *   when the stage carries no switches.
 * @returns {number} decks to put on one page, 1..PHONE_DECK_MAX_PER_PAGE
 */
function phoneDeckPageSize({ listRoomPx, contentHeightPx, preferencesHeightPx } = {}) {
  let room = finite(listRoomPx);

  if (room === null) {
    const content = finite(contentHeightPx);
    /* NOTHING MEASURED means this is not a box we can reason about — desktop, a
       detached render, jsdom, a stage that has not been laid out yet. The
       designed page is the honest answer; collapsing to one deck because a
       measurement failed would be a wall built out of a missing number.

       A height of ZERO is that same situation and not a real box: jsdom reports
       `clientHeight` as 0 for every element, and an unlaid-out content area does
       too. Reading it as "no room" paged one deck everywhere it appeared. */
    if (content === null || content <= 0) return PHONE_DECK_MAX_PER_PAGE;
    const prefs = finite(preferencesHeightPx) ?? 0;
    room = content - HEADER_BLOCK_PX - PAGER_BLOCK_PX
      - (prefs > 0 ? prefs + PREFERENCES_MARGIN_PX : 0);
  }

  const fits = Math.floor((room + DECK_ROW_GAP_PX) / (DECK_ROW_PX + DECK_ROW_GAP_PX));
  // A page of zero decks is a dead screen: a pager with nothing to pick. One
  // row that slightly overhangs beats no row at all.
  return Math.max(1, Math.min(PHONE_DECK_MAX_PER_PAGE, fits));
}

export {
  phoneDeckPageSize,
  PHONE_DECK_MAX_PER_PAGE,
  DECK_ROW_PX,
  DECK_ROW_GAP_PX,
  HEADER_BLOCK_PX,
  PAGER_BLOCK_PX,
  PREFERENCES_MARGIN_PX,
  PHONE_PREFERENCES_ROW_PX,
};
